import { useEffect, useState } from "react"

export type Theme = "light" | "dark"

const STORAGE_KEY = "eyesight:theme"

function systemPreference(): Theme {
  if (typeof window === "undefined") return "dark"
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark"
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === "light" || v === "dark" ? v : null
  } catch {
    return null
  }
}

function apply(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme)
}

export function useTheme(): { theme: Theme; toggle: () => void; set: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(() => readStored() ?? systemPreference())

  useEffect(() => {
    apply(theme)
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      /* ignore */
    }
  }, [theme])

  // Track system changes only if the user hasn't pinned a preference.
  useEffect(() => {
    if (readStored()) return
    const mq = window.matchMedia?.("(prefers-color-scheme: light)")
    if (!mq) return
    const listener = (e: MediaQueryListEvent) => setTheme(e.matches ? "light" : "dark")
    mq.addEventListener("change", listener)
    return () => mq.removeEventListener("change", listener)
  }, [])

  return {
    theme,
    toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    set: setTheme,
  }
}
